import React, { Component } from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts/highstock';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';

import withChartExport from '../../../components/ChartExporting';
import dateUtils, { getWorkMinutesPerDay, formatWorkDuration, MINUTES_PER_DAY } from '../../../util/dates';
import { isWhite } from '../../../util/colors';
import { escapeHTML } from '../../../util/text';
import _ from 'lodash';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

let chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'column',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  navigator: {
    enabled: false
  },
  rangeSelector: {
    enabled: false
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    tickmarkPlacement: 'on',
    labels: {
      enabled: true,
      style: {
        fontSize: '12px'
      }
    },
    title: {
      enabled: false
    }
  },
  yAxis: {
    min: 0,
    opposite: false,
    allowDecimals: false,
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Cycle time (days)',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      style: {
        fontSize: '12px'
      },
      formatter: function() {
        return Math.ceil(this.value / MINUTES_PER_DAY);
      }
    },
    plotLines: []
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      shadow: false,
      cropThreshold: 10000,
      stickyTracking: false,
      cursor: 'pointer',
      point: {
        events: {}
      }
    },
    column: {
      dataLabels: {
        enabled: false,
        style: {
          fontSize: '11px',
          fontWeight: 'normal',
          color: 'contrast'
        },
        formatter: function() {
          return Highcharts.numberFormat(this.percentage) + '%';
        }
      },
      stacking: 'normal',
      pointPadding: 0,
      borderWidth: 1,
      groupPadding: 0,
      shadow: false,
      pointRange: 24 * 3600 * 1000     
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    shared: true,
    hideDelay: 0,
    stickOnContact: true,
    borderRadius: 5,
    borderWidth: 0
  },
  series: [
    {
      name: 'Data',
      data: []
    }
  ]
};

export class CTBChart extends Component {
  static defaultProps = {
    throughput: []
  };

  constructor(props) {
    super(props);
    this.state = {
      config: {}
    };
  }

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
    highchartFactory(Highcharts);

    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };
    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };

    chartConfig.exporting = this.props.chartExporting;

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);
    this.updateYAxisFormatter();

    if (this.props.data && this.props.data.length) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.updateChart(prevProps.data);
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (!_.isEqual(prevProps.excludeWeekendOpts, this.props.excludeWeekendOpts)) {
      this.updateYAxisFormatter();
    }

    if (prevProps.breakdown != this.props.breakdown) {
      this.togglePercentage();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Cycle Time Breakdown Chart',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },
              plotOptions: {
                column: {
                  dataLabels: {
                    style: {
                      textOutline: 'none'
                    }
                  }
                }
              }
            }
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChart() {
    this.chart.xAxis[0].setExtremes(null, null);

    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }
    for (let i = 0; i < this.props.data.length; i++) {
      this.chart.addSeries(this.props.data[i], false);
    }

    this.updateTimeUnit(this.props.timeUnit);
    this.togglePercentage();
    this.chart.xAxis[0].setExtremes(null, null);
    this.chart.redraw();    
  }

  togglePercentage() {
    let enabled = false;
    if (this.props.breakdown && this.chart.series.length && this.chart.series[0].columnMetrics) {
      enabled = this.chart.series[0].columnMetrics.width >= 40;
    }

    this.chart.update({
      plotOptions: {
        column: {
          dataLabels: {
            enabled
          }
        }
      }
    });
  }

  updateTimeUnit(timeUnit) {
    if (timeUnit === 'cards') {
      this.chart.update({
        xAxis: {
          type: 'category',
          labels: {
            enabled: false
          }
        },
        plotOptions: {
          column: {
            pointRange: 1
          }
        }
      });

      return false;
    }

    let days = 1,
      ordinal = false,
      min,
      max,
      placement;
    switch (timeUnit) {
      case 'week': {
        days = 7;
        min = +moment(this.props.dates.startDate).startOf('week');
        max = +new Date(this.props.dates.endDate).endOf('week');
        break;
      }
      case '2weeks': {
        days = 14;
        placement = 'between';
        min = +moment(this.props.dates.startDate).startOf('week');
        max = +new Date(this.props.dates.endDate).endOf('week');
        break;
      }
      case 'month': {
        days = 30;
        ordinal = true;
        min = +moment(this.props.dates.startDate).date(1);
        max = +moment(this.props.dates.endDate).date(30);
        break;
      }
    }

    this.chart.update({
      xAxis: {
        ordinal,
        type: 'datetime',
        labels: {
          enabled: true
        }
      },
      plotOptions: {
        column: {
          pointRange: days * 24 * 3600 * 1000,
          pointPlacement: placement
        }
      }
    });

    this.updateChartRange(min, max);
  }

  updateTimeLabelsFormat(dateFormat) {
    let config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.chart.update(config);
  }

  updateYAxisFormatter() {
    const workMinutesPerDay = getWorkMinutesPerDay(this.props.excludeWeekendOpts, this.props.useWorkingHoursInCycleTimeDays);
    this.chart.update({
      yAxis: {
        labels: {
          formatter: function() {
            return Math.ceil(this.value / workMinutesPerDay);
          }
        }
      }
    });
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('ctb', this.props.wallmode);
    this.chart.update(newConfig.chart);
  }

  updateChartRange(minVal, maxVal) {
    const min = minVal || +this.props.dates.startDate;
    const max = maxVal || +this.props.dates.endDate;

    const xExtremes = {
      min,
      max
    };
    this.setExtremes(xExtremes);
  }

  setExtremes(xExtremesObject, yExtremesObject) {
    if (xExtremesObject) {
      this.chart.xAxis[0].setExtremes(xExtremesObject.min, xExtremesObject.max);
    }
    if (yExtremesObject) {
      this.chart.yAxis[0].setExtremes(yExtremesObject.min, yExtremesObject.max);
    } else {
      this.chart.yAxis[0].setExtremes(null, null);
    }
  }

  getDateString(date) {
    let str = moment(date).format(this.props.dateFormat);
    if (this.props.timeUnit === 'week') {
      str = `${moment(date)
        .startOf('isoWeek')
        .format(this.props.dateFormat)} - ${moment(date)
        .endOf('isoWeek')
        .format(this.props.dateFormat)}`;
    } else if (this.props.timeUnit === '2weeks') {
      str = `${moment(date).format(this.props.dateFormat)} - ${moment(date)
        .add(13, 'days')
        .format(this.props.dateFormat)}`;
    } else if (this.props.timeUnit === 'month') {
      str = `${moment(date)
        .startOf('month')
        .format(this.props.dateFormat)} - ${moment(date)
        .endOf('month')
        .format(this.props.dateFormat)}`;
    }

    return str;
  }

  onPointClick(comp, ev) {
    let points = [];
    const date = comp.getDateString(ev.x);
    let duration = 0;

    for (let s = 0; s < this.chart.series.length; s++) {
      for (let p = 0; p < this.chart.series[s].points.length; p++) {
        if (ev.x == this.chart.series[s].points[p].x && this.chart.series[s].points[p].details) {
          duration += this.chart.series[s].points[p].y;
          for (let i = 0; i < this.chart.series[s].points[p].details.length; i++) {
            const point = this.chart.series[s].points[p].details[i];
            points.push({
              card: point.card,
              details: point.card.details,
              color: point.card.color
            });
          }
        }
      }
    }

    points = _.uniqBy(points, p => {
      return p.card.id;
    });

    const tPoint = _.find(comp.props.throughput, { x: ev.x });
    const totalThroughput = tPoint ? tPoint.y : 0;

    comp.props.onPointClick({
      date: comp.props.timeUnit === 'cards' ? ev.card : date,
      value: formatWorkDuration(duration, comp.props.excludeWeekendOpts, comp.props.timePrecision, comp.props.useWorkingHoursInCycleTimeDays),
      value2: totalThroughput,
      points
    });
  }

  tooltipFormatter(comp, ev) {
    let details = '';

    for (let s = 0; s < ev.points.length; s++) {
      const point = ev.points[s];
      const duration = formatWorkDuration(point.y, this.props.excludeWeekendOpts, this.props.timePrecision, this.props.useWorkingHoursInCycleTimeDays);
      const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
      details += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background:${point.color}"></div>${escapeHTML(point.series.name)}: ${duration}</div>`;
    }

    const totalCt = _.sumBy(ev.points, 'y');
    const totalDuration = formatWorkDuration(totalCt, this.props.excludeWeekendOpts, this.props.timePrecision, this.props.useWorkingHoursInCycleTimeDays);

    const date = comp.getDateString(ev.x);
    const name = ev.points[0].point.card;
    const field = comp.props.timeUnit === 'cards' ? 'Name' : 'Date';
    const value = comp.props.timeUnit === 'cards' ? name : date;
    let tooltip = `<p class="tooltip-label-title-size" style="margin: 8px 8px 0;">${field}: ${value}</p> 
      <p class="tooltip-label-size" style="margin: 4px 8px 0;">Cycle Time: ${totalDuration}</p>`;
    if (comp.props.timeUnit !== 'cards') {
      const tPoint = _.find(comp.props.throughput, { x: ev.x });
      const totalThroughput = tPoint ? tPoint.y : 0;
      tooltip += `<p class="tooltip-label-size" style="margin: 4px 8px 8px;">Throughput: ${totalThroughput}</p>`;
    }
    // Highcharts clips the tooltip to the chart container, so the list must fit inside the chart to be scrollable
    const detailsMaxHeight = Math.max(comp.chart.chartHeight - 130, 60);
    tooltip += `<div class="tooltip-list-scroll" style="max-height: ${detailsMaxHeight}px;">${details}</div>`;
    tooltip += `<p class="tooltip-info-txt">Click on the bar to see the details</p>`;

    return tooltip;
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div id={`${this.props.id}-details`} className={'chart_details chart_details-full no-stroke'} />
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    timePrecision: state.filters.timePrecision,
    timeUnit: state.filters.ctb.timeUnit,
    breakdown: state.filters.ctb.breakdown,
    wallmode: state.app.wallmode,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps)(CTBChart));
